import { useState, useCallback, useContext } from "react";
import useTranslation from "../../../../../packages/locales/translations/util/useTranslation";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { MediaTasksModal } from "./MediaTasksModal/MediaTasksModal";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { ModalContext } from "@coworker/apprestructured/src/layout/context/ModalContextProvider";
import { ModalType } from "@coworker/apprestructured/src/layout/context/modalType";
import { TaskCardRenderer } from "./TaskCardRenderer";
import { MediaTaskListItem, MediaTasksData } from "./types";
import styles from "./media-tasks-section.module.css";

interface MediaTasksSectionProps {
  data: MediaTasksData;
}

export const MediaTasksSection = ({ data }: MediaTasksSectionProps) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [, setSearchParams] = useSearchParamState();
  const { toggleModal } = useContext(ModalContext);

  // Destructure all data from props
  const {
    allTasks,
    totalTaskCount,
    isLoading,
    itemsMap,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = data;

  const handleTaskClick = useCallback(
    (taskListItem: MediaTaskListItem) => {
      if (taskListItem.type === "v1") {
        // V1 tasks: Use old modal
        toggleModal({
          type: ModalType.TaskDetail,
          payload: {
            taskId: taskListItem.task.id,
            taskType: taskListItem.task.task_type,
          },
        });
      } else {
        // V2 tasks: Use new TaskViewerSheet
        setSearchParams((params) => {
          params.set("taskViewer", String(taskListItem.task.id));
        });
      }
    },
    [toggleModal, setSearchParams]
  );

  const handleV1TaskClick = useCallback(
    (taskId: string, taskType: string) => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId, taskType },
      });
    },
    [toggleModal]
  );

  const count = allTasks.length;

  // Render logic
  if (isLoading && count === 0) {
    return null; // Still loading, no cached data
  }

  if (count === 0) {
    return null; // No tasks to display
  }

  // Single task: render inline
  if (count === 1 && allTasks[0]) {
    return (
      <div className={styles["single-task-container"]}>
        <ul className={styles["task-list"]}>
          <TaskCardRenderer
            taskListItem={allTasks[0]}
            itemsMap={itemsMap}
            onV1TaskClick={handleV1TaskClick}
            onV2TaskClick={handleTaskClick}
          />
        </ul>
      </div>
    );
  }

  // Multiple tasks: render clickable row + modal
  // Only show Load More when total > 20 AND there are more v2 tasks to load
  const shouldShowLoadMore = totalTaskCount > 20 && hasNextPage;

  return (
    <>
      <FixaListView id="media-tasks-list">
        <FixaListViewItem
          size="medium"
          control="navigational"
          controlIcon="chevronRight"
          inset
          onClick={() => setShowModal(true)}
          id="media-tasks-list-item"
          data-testid="media-tasks-list-item"
          title={
            <FixaText
              textStyle="Heading.XS"
              className={styles["tasks-heading"]}
            >
              {t("TasksString")}
            </FixaText>
          }
          description={`${t("relatedTasksCountString", { count: totalTaskCount })} for article`}
        />
      </FixaListView>
      <MediaTasksModal
        tasks={allTasks}
        showModal={showModal}
        onClose={() => setShowModal(false)}
        totalCount={totalTaskCount}
        hasNextPage={shouldShowLoadMore}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />
    </>
  );
};
